// ── Librerías externas ───────────────────────────────────────────────────
import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { MapContainer, TileLayer, Marker, useMap, useMapEvents } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

// ── Heroicons ────────────────────────────────────────────────────────────
import { ExclamationCircleIcon } from '@heroicons/react/24/outline';

// ── Servicios y contexto ─────────────────────────────────────────────────
import { getClaseServicio, getTipoServicio, getDepartamentos, getMunicipios, getLugares } from '../services/azureApi';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';

// ── Constantes ───────────────────────────────────────────────────────────
import { URGENCIAS, COD_DEPARTAMENTO_JALAPA, LAT_DEFAULT, LON_DEFAULT } from '../constants/incidentes';

// ── Assets y estilos ─────────────────────────────────────────────────────
import './Incidentes.css';

// ── Fix ícono por defecto de Leaflet con webpack/vite ────────────────────
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl:       'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl:     'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

// ════════════════════════════════════════════════════════════════════════
// HELPERS
// ════════════════════════════════════════════════════════════════════════

/** Genera las iniciales del nombre de usuario para el avatar. */
// function iniciales(nombre = '') {
//   const partes = nombre.split(/[.\s_-]/);
//   return partes.slice(0, 2).map(p => p[0]?.toUpperCase() || '').join('') || '??';
// }

/**
 * Devuelve color, fondo y abreviatura según la descripción de la clase de servicio.
 * Se usa para los badges del combobox.
 */
function categoriaPorClase(nombre = '') {
  const n = nombre.toLowerCase();
  if (n.includes('incendio') || n.includes('conato') || n.includes('forestal'))
    return { color: '#c0392b', bg: '#fdeaea', abbr: 'INC', icon: '🔥' };
  if (n.includes('rescate') || n.includes('barranco') || n.includes('extraviad') || n.includes('rastreo'))
    return { color: '#e67e22', bg: '#fef3e8', abbr: 'RES', icon: '🆘' };
  if (n.includes('tránsito') || n.includes('transito') || n.includes('motorista') ||
      n.includes('atropellad') || n.includes('vehículo') || n.includes('vehiculo') ||
      n.includes('aéreo') || n.includes('aereo'))
    return { color: '#2980b9', bg: '#eaf3fb', abbr: 'TRA', icon: '🚗' };
  if (n.includes('médica') || n.includes('medica') || n.includes('traslado') ||
      n.includes('enfermedad') || n.includes('intoxicad') || n.includes('maternidad') ||
      n.includes('quemad') || n.includes('electrocutad') || n.includes('caída') ||
      n.includes('caida') || n.includes('mordid') || n.includes('fallecido') ||
      n.includes('violencia') || n.includes('agredid') || n.includes('recién') || n.includes('trabajo'))
    return { color: '#27ae60', bg: '#eafaf1', abbr: 'MED', icon: '🏥' };
  if (n.includes('agua') || n.includes('inundad') || n.includes('soterada') || n.includes('acuático'))
    return { color: '#1a6fa3', bg: '#e8f4fd', abbr: 'AGU', icon: '💧' };
  if (n.includes('animal') || n.includes('enjambre'))
    return { color: '#8e44ad', bg: '#f5eef8', abbr: 'ANI', icon: '🐾' };
  if (n.includes('prevención') || n.includes('prevencion') || n.includes('falsa'))
    return { color: '#7f8c8d', bg: '#f4f6f7', abbr: 'PRV', icon: '🛡️' };
  return { color: '#555', bg: '#f4f4f4', abbr: 'OTR', icon: '⚡' };
}

// ════════════════════════════════════════════════════════════════════════
// SUB-COMPONENTES
// ════════════════════════════════════════════════════════════════════════

// Reposiciona el mapa solo cuando cambia el centro (geocodificación), no en cada clic del usuario.
// eslint-disable-next-line react/prop-types
function MapView({ lat, lon }) {
  const map = useMap();
  useEffect(() => { map.setView([lat, lon], 14); }, [lat, lon, map]);
  return null;
}

// eslint-disable-next-line react/prop-types
function MapClickHandler({ onClic }) {
  const map = useMap();
  const timer = React.useRef(null);
  useMapEvents({
    click: e => {
      // Debounce 200 ms: si viene dblclick cancela el clic simple
      timer.current = setTimeout(() => onClic(e.latlng.lat, e.latlng.lng), 200);
    },
    dblclick: e => {
      clearTimeout(timer.current);
      map.setView(e.latlng, map.getZoom() + 2);
    },
  });
  return null;
}

// ════════════════════════════════════════════════════════════════════════
// COMPONENTE PRINCIPAL
// ════════════════════════════════════════════════════════════════════════

export default function Incidentes() {
  const { sesion } = useAuth();
  const navigate = useNavigate();

  // ── Flag para evitar peticiones duplicadas en React Strict Mode ────────
  const hasInitialized = React.useRef(false);

  // ── Catálogo (desde Azure) ───────────────────────────────────────────
  const [clases,        setClases]        = useState([]);
  const [tiposServicio, setTiposServicio] = useState([]);
  const [departamentos, setDepartamentos] = useState([]);
  const [municipios,    setMunicipios]    = useState([]);
  const [todosLugares,  setTodosLugares]  = useState([]);
  const [loadingCat,    setLoadingCat]    = useState(true);
  const [errorCat,      setErrorCat]      = useState('');

  // ── Buscador / grid clase de servicio ───────────────────────────────
  const [busquedaClase, setBusquedaClase] = useState('');
  const [mostrarTodas,  setMostrarTodas]  = useState(false);

  const CARDS_VISIBLES = 10;

  // ── Campos del formulario ────────────────────────────────────────────
  const [claseIdx,          setClaseIdx]          = useState(null);
  const [tipoServIdx,       setTipoServIdx]       = useState(null);
  const [urgencia,          setUrgencia]          = useState(3);
  const [departamento,      setDepartamento]      = useState('');
  const [municipio,         setMunicipio]         = useState('');
  const [area,              setArea]              = useState('Urbana');
  const [zona,              setZona]              = useState(0);
  const [codLugar,          setCodLugar]          = useState('');
  const [direccion,         setDireccion]         = useState('');
  const [unidades,          setUnidades]          = useState('1 Unidad');
  const [nombreSolicitante, setNombreSolicitante] = useState('');
  const [observaciones,     setObservaciones]     = useState('');

  // ── Mapa ─────────────────────────────────────────────────────────────
  const [latitud,        setLatitud]        = useState(LAT_DEFAULT);
  const [longitud,       setLongitud]       = useState(LON_DEFAULT);
  // Centro del mapa (solo cambia al geocodificar, no en cada clic del usuario)
  const [mapCenter,      setMapCenter]      = useState({ lat: LAT_DEFAULT, lon: LON_DEFAULT });
  const [geocodificando, setGeocodificando] = useState(false);

  // ── Combobox lugar ───────────────────────────────────────────────────
  const [busquedaLugar,     setBusquedaLugar]     = useState('');
  const [lugarDropdownOpen, setLugarDropdownOpen] = useState(false);
  const [dropdownPos,       setDropdownPos]       = useState({});
  const lugarRef      = React.useRef(null);
  const lugarInputRef = React.useRef(null);

  // ── Modal confirmación ───────────────────────────────────────────────
  const [modalOpen, setModalOpen] = useState(false);

  // ── Envío ─────────────────────────────────────────────────────────────
  const [enviando, setEnviando] = useState(false);
  const [error,    setError]    = useState('');
  const [exito,    setExito]    = useState('');

  // ════════════════════════════════════════════════════════════════════
  // EFECTOS
  // ════════════════════════════════════════════════════════════════════

  /** Carga catálogos, departamentos y lugares al montar (solo una vez, evita duplicados en Strict Mode). */
  useEffect(() => {
    if (hasInitialized.current) return;
    hasInitialized.current = true;

    const codCompania = sesion?.cod_compania?.[0] ?? 44;

    Promise.all([
      getClaseServicio(codCompania),
      getTipoServicio(codCompania),
      getDepartamentos(),
      getLugares(),
    ])
      .then(([claseRes, tipoRes, deptoRes, lugarRes]) => {
        setClases(claseRes.data || []);
        setTiposServicio(tipoRes.data || []);
        const deptos = Array.isArray(deptoRes.data) ? deptoRes.data : [];
        setDepartamentos(deptos);
        // Default: Jalapa
        const defaultDepto = deptos.find(d => d.Cod_Depto === String(COD_DEPARTAMENTO_JALAPA)) ?? deptos[0];
        if (defaultDepto) setDepartamento(defaultDepto.Cod_Depto);
        setTodosLugares(Array.isArray(lugarRes.data) ? lugarRes.data : []);
      })
      .catch(err => {
        const msg = err.response
          ? `Error ${err.response.status}: ${JSON.stringify(err.response.data)}`
          : err.message;
        setErrorCat(`No se pudo cargar el catálogo. ${msg}`);
      })
      .finally(() => setLoadingCat(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /** Recarga municipios cuando cambia el departamento. */
  useEffect(() => {
    if (!departamento) return;
    getMunicipios(departamento)
      .then(({ data }) => {
        const lista = Array.isArray(data) ? data : [];
        setMunicipios(lista);
        setMunicipio(lista.length > 0 ? lista[0].Cod_Muni : '');
      })
      .catch(() => { setMunicipios([]); setMunicipio(''); });
  }, [departamento]);

  /** Centra el mapa en el municipio seleccionado vía Nominatim. */
  useEffect(() => {
    if (!municipio || municipios.length === 0) return;
    const nombreMuni = municipios.find(m => m.Cod_Muni === municipio)?.Nombre_Muni;
    if (!nombreMuni) return;
    const nombreDepto = departamentos.find(d => d.Cod_Depto === departamento)?.Nombre_Depto || 'Jalapa';
    setGeocodificando(true);
    fetch(`https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(`${nombreMuni}, ${nombreDepto}, Guatemala`)}&format=json&limit=1&countrycodes=gt`, { headers: { 'User-Agent': 'PIAPO/1.0' } })
      .then(r => r.json())
      .then(data => {
        if (data.length > 0) {
          const lat = parseFloat(data[0].lat);
          const lon = parseFloat(data[0].lon);
          setLatitud(lat);
          setLongitud(lon);
          setMapCenter({ lat, lon });
        }
      })
      .catch(() => {})
      .finally(() => setGeocodificando(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [municipio, municipios]);

  /** Geocodifica el lugar seleccionado y mueve el marcador al mapa. */
  useEffect(() => {
    if (!codLugar) return;
    const lugar = todosLugares.find(l => String(l.Cod_Lugar) === codLugar);
    if (!lugar) return;
    setGeocodificando(true);
    const query = `${lugar.Descripcion}, ${lugar.Nombre_Muni}, ${lugar.Nombre_Depto}, Guatemala`;
    fetch(`https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(query)}&format=json&limit=1&countrycodes=gt`, { headers: { 'User-Agent': 'PIAPO/1.0' } })
      .then(r => r.json())
      .then(data => {
        if (data.length > 0) {
          const lat = parseFloat(data[0].lat);
          const lon = parseFloat(data[0].lon);
          setLatitud(lat);
          setLongitud(lon);
          setMapCenter({ lat, lon });
        }
      })
      .catch(() => {})
      .finally(() => setGeocodificando(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [codLugar]);



  /** Limpia el texto del combobox cuando la selección se cancela externamente. */
  useEffect(() => {
    if (!codLugar) { setBusquedaLugar(''); setLugarDropdownOpen(false); }
  }, [codLugar]);

  /** Cierra el dropdown de lugar al hacer clic fuera. */
  useEffect(() => {
    const handler = e => {
      if (lugarRef.current && !lugarRef.current.contains(e.target))
        setLugarDropdownOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  /** Recalcula la posición del dropdown al hacer scroll (capture=true atrapa cualquier contenedor). */
  useEffect(() => {
    if (!lugarDropdownOpen) return;
    const update = () => {
      if (lugarInputRef.current) {
        const r = lugarInputRef.current.getBoundingClientRect();
        setDropdownPos({ top: r.bottom + 4, left: r.left, width: r.width });
      }
    };
    window.addEventListener('scroll', update, true);
    return () => window.removeEventListener('scroll', update, true);
  }, [lugarDropdownOpen]);

  // ════════════════════════════════════════════════════════════════════
  // ACCIONES
  // ════════════════════════════════════════════════════════════════════

  const urgSel = URGENCIAS[urgencia - 1];

  const abrirDropdownLugar = () => {
    if (lugarInputRef.current) {
      const r = lugarInputRef.current.getBoundingClientRect();
      setDropdownPos({ top: r.bottom + 4, left: r.left, width: r.width });
    }
    setLugarDropdownOpen(true);
  };

  const handleAbrirModal = () => {
    if (!direccion.trim())     { setError('La dirección es obligatoria.');         return; }
    if (!observaciones.trim()) { setError('Las observaciones son obligatorias.');   return; }
    setError('');
    setModalOpen(true);
  };

  const handleConfirmar = async () => {
    setModalOpen(false);
    setEnviando(true);

    const claseSelec = clases[claseIdx]          ?? null;
    const tipoSelec  = tiposServicio[tipoServIdx] ?? null;

    const base = {
      Cod_Compania:       sesion?.cod_compania?.[0] ? Number(sesion.cod_compania[0]) : null,
      Cod_Servicio:       tipoSelec?.Cod_Servicio        ?? null,
      Cod_Clase_Servicio: claseSelec?.Cod_Clase_Servicio ?? null,
      Fecha_Servicio:     new Date().toISOString(),
      Nombre_Solicitante: nombreSolicitante || null,
      Observaciones:      observaciones,
      Usuario_Registra:   sesion?.usuario ?? null,
      Cod_Muni:           municipio || null,
      Cod_Lugar:          codLugar ? Number(codLugar) : null,
      Area:               area,
      Zona:               zona,
      Direccion:          direccion,
      Latitud:            latitud,
      Longitud:           longitud,
    };

    // Payload para la BD local — incluye textos del catálogo para mostrar en Despacho sin lookup
    const payloadLocal = {
      ...base,
      Prioridad:          urgSel.prioridad,
      Unidades:           unidades,
      Des_Tipo_Servicio:  tipoSelec?.Descripcion_Servicio ?? null,
      Des_Clase_Servicio: claseSelec?.Des_Clase_Servicio  ?? null,
    };

    // Payload para Azure — solo los campos que acepta Azure
    const payloadAzure = {
      ...base,
      Zona:          String(zona),
      Cod_Prioridad: String(urgSel.nivel),
      Des_Unidades:  unidades,
    };

    try {
      // 1. Guardar en BD local y obtener el ID local
      const { data: servicioLocal } = await api.post('/servicios', payloadLocal);

      // 2. Guardar en Azure via proxy local (evita CORS) y obtener NoControl
      let azureOk = false;
      try {
        const { data: azureResp } = await api.post('/MinRescate', payloadAzure);
        if (azureResp?.codError === '200' && azureResp?.msgRespuesta && servicioLocal?.id) {
          await api.patch(`/servicios/${servicioLocal.id}/noControl`, { NoControl: Number(azureResp.msgRespuesta) });
          azureOk = true;
        }
      } catch (azureErr) {
        console.warn('Servicio guardado localmente pero no en Azure:', azureErr.message);
      }

      setExito(azureOk
        ? '✅ Incidente registrado correctamente. Redirigiendo...'
        : '⚠️ Incidente guardado localmente (Azure no disponible). Redirigiendo...');

      setTimeout(() => navigate('/respuestas'), 1500);
    } catch (err) {
      setError(err.response?.data?.mensaje || err.response?.data?.msgRespuesta || 'Error al registrar el incidente.');
      setEnviando(false);
    }
  };

  // ════════════════════════════════════════════════════════════════════
  // RENDER
  // ════════════════════════════════════════════════════════════════════

  const claseSeleccionada = claseIdx !== null ? clases[claseIdx] : null;
  const tipoSeleccionado  = tipoServIdx !== null ? tiposServicio[tipoServIdx] : null;
  const deptoNombre       = departamentos.find(d => d.Cod_Depto === departamento)?.Nombre_Depto || '—';
  const muniNombre        = municipios.find(m => m.Cod_Muni === municipio)?.Nombre_Muni || '—';

  const seccion1Completa = tipoServIdx !== null || claseIdx !== null;
  const seccion2Completa = !!direccion.trim();
  const seccion3Completa = !!observaciones.trim();

  return (
    <div className="inc-page">

      {/* ── PAGE HEADER ─────────────────────────────────────────── */}
      <div className="inc-page-header">
        <div className="inc-page-header-left">
          <div className="inc-page-icon">
            <ExclamationCircleIcon />
          </div>
          <div>
            <div className="inc-page-title">Nueva Emergencia</div>
            <div className="inc-page-sub">Cía. 44 Jalapa · Completa los datos para el despacho</div>
          </div>
        </div>
        <Link to="/" className="inc-back-btn">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M10 19l-7-7m0 0l7-7m-7 7h18"/>
          </svg>
          Dashboard
        </Link>
      </div>

      {/* ── STEP INDICATOR ──────────────────────────────────────── */}
      <div className="inc-steps">
        <div className={`inc-step${seccion1Completa ? ' done' : ' active'}`}>
          <span className="inc-step-num">{seccion1Completa ? '✓' : '1'}</span>
          <span>Tipo de Emergencia</span>
        </div>
        <div className={`inc-step${seccion2Completa ? ' done' : seccion1Completa ? ' active' : ''}`}>
          <span className="inc-step-num">{seccion2Completa ? '✓' : '2'}</span>
          <span>Ubicación</span>
        </div>
        <div className={`inc-step${seccion3Completa ? ' done' : seccion2Completa ? ' active' : ''}`}>
          <span className="inc-step-num">{seccion3Completa ? '✓' : '3'}</span>
          <span>Detalles</span>
        </div>
      </div>

      {/* ── LAYOUT DOS COLUMNAS ─────────────────────────────────── */}
      <div className="inc-layout">

        {/* COLUMNA IZQUIERDA — FORMULARIO */}
        <div className="inc-form">

          {/* ── CARD 1: TIPO DE EMERGENCIA ──────────────────────── */}
          <div className="inc-card">
            <div className="inc-card-header">
              <div className="inc-card-num">1</div>
              <div className="inc-card-header-text">
                <h3>Tipo de Emergencia</h3>
                <p>Selecciona el servicio y la clase del incidente</p>
              </div>
            </div>
            <div className="inc-card-body">

              {tiposServicio.length > 0 && (
                <div className="mb-4">
                  <label className="form-label">Tipo de Servicio</label>
                  <select
                    className="form-input"
                    value={tipoServIdx ?? ''}
                    onChange={e => setTipoServIdx(Number(e.target.value))}
                  >
                    <option value="" disabled>Selecciona un tipo de servicio</option>
                    {tiposServicio.map((t, i) => (
                      <option key={t.Cod_Servicio} value={i}>{t.Descripcion_Servicio}</option>
                    ))}
                  </select>
                </div>
              )}

              <div className="mb-4">
                <label className="form-label">
                  Clase de Incidente&nbsp;
                  <span style={{ fontWeight: 400, textTransform: 'none', color: '#aaa', fontSize: '.72rem' }}>(opcional)</span>
                </label>

                {loadingCat ? (
                  <div className="clase-grid-skeleton">
                    {Array.from({ length: 6 }).map((_, i) => <div key={i} className="skeleton-item skeleton-card" />)}
                  </div>
                ) : errorCat ? (
                  <div className="error-box">
                    <span style={{ fontSize: '1.2rem' }}>⚠️</span>
                    <div>
                      <strong>Error al cargar tipos de incidente</strong>
                      <div style={{ marginTop: 4, fontSize: '.82rem', opacity: .85 }}>{errorCat}</div>
                    </div>
                  </div>
                ) : (() => {
                  const clasesFiltradas = clases
                    .map((c, i) => ({ c, i }))
                    .filter(({ c }) =>
                      busquedaClase.trim() === ''
                        ? true
                        : c.Des_Clase_Servicio.toLowerCase().includes(busquedaClase.toLowerCase())
                    );
                  const visibles = mostrarTodas ? clasesFiltradas : clasesFiltradas.slice(0, CARDS_VISIBLES);
                  const hayMas   = clasesFiltradas.length > CARDS_VISIBLES;

                  return (
                    <>
                      <div className="clase-input-row mb-3">
                        <span className="clase-search-ico">
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <circle cx="11" cy="11" r="8"/><path d="M21 21l-4.35-4.35"/>
                          </svg>
                        </span>
                        <input
                          type="text"
                          className="form-input"
                          placeholder="Buscar tipo de incidente..."
                          value={busquedaClase}
                          onChange={e => { setBusquedaClase(e.target.value); setClaseIdx(null); setMostrarTodas(false); }}
                          style={{ paddingLeft: 36 }}
                        />
                        {busquedaClase && (
                          <button className="clase-clear-btn" onClick={() => { setBusquedaClase(''); setClaseIdx(null); }} title="Limpiar búsqueda">×</button>
                        )}
                      </div>

                      {clasesFiltradas.length === 0 ? (
                        <div className="clase-empty">Sin resultados para &ldquo;{busquedaClase}&rdquo;</div>
                      ) : (
                        <>
                          <div className="clase-grid">
                            {visibles.map(({ c, i }) => {
                              const cat = categoriaPorClase(c.Des_Clase_Servicio);
                              return (
                                <div
                                  key={c.Cod_Clase_Servicio}
                                  className={`clase-card${claseIdx === i ? ' sel' : ''}`}
                                  style={claseIdx === i ? { borderColor: cat.color, background: cat.bg } : {}}
                                  onClick={() => setClaseIdx(claseIdx === i ? null : i)}
                                >
                                  <div className="clase-card-icon">{cat.icon}</div>
                                  <div className="clase-card-label">{c.Des_Clase_Servicio.trim()}</div>
                                  {claseIdx === i && (
                                    <div className="clase-card-check" style={{ background: cat.color }}>✓</div>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                          {hayMas && !busquedaClase && (
                            <button className="clase-show-more" onClick={() => setMostrarTodas(v => !v)}>
                              {mostrarTodas ? '▲ Mostrar menos' : `▼ Mostrar más (${clasesFiltradas.length - CARDS_VISIBLES} más)`}
                            </button>
                          )}
                        </>
                      )}
                    </>
                  );
                })()}
              </div>
            </div>
          </div>

          {/* ── CARD 2: UBICACIÓN ───────────────────────────────── */}
          <div className="inc-card">
            <div className="inc-card-header">
              <div className="inc-card-num">2</div>
              <div className="inc-card-header-text">
                <h3>Ubicación del Incidente</h3>
                <p>Departamento, municipio y dirección exacta</p>
              </div>
            </div>
            <div className="inc-card-body">

              <div className="mb-3">
                <label className="form-label">Departamento</label>
                <select
                  className="form-input"
                  value={departamento}
                  onChange={e => { setDepartamento(e.target.value); setCodLugar(''); }}
                >
                  {departamentos.length === 0 && <option value="">Cargando...</option>}
                  {departamentos.map(d => (
                    <option key={d.Cod_Depto} value={d.Cod_Depto}>{d.Nombre_Depto}</option>
                  ))}
                </select>
              </div>

              <div className="row-2 mb-3">
                <div>
                  <label className="form-label">Municipio</label>
                  <select
                    className="form-input"
                    value={municipio}
                    onChange={e => { setMunicipio(e.target.value); setCodLugar(''); }}
                  >
                    {municipios.length === 0 && <option value="">Cargando...</option>}
                    {municipios.map(m => (
                      <option key={m.Cod_Muni} value={m.Cod_Muni}>{m.Nombre_Muni}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="form-label">Área</label>
                  <div className="area-toggle">
                    {['Urbana', 'Rural'].map(op => (
                      <button
                        key={op}
                        type="button"
                        className={`area-btn${area === op ? ' active' : ''}`}
                        onClick={() => { setArea(op); setCodLugar(''); }}
                      >
                        {op === 'Urbana' ? '🏙️' : '🌄'} {op}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              {(() => {
                const lugaresDisponibles = todosLugares.filter(
                  l => (!municipio || l.Cod_Muni === municipio) && l.Area.trim() === area
                );
                return (
                  <div className={`${lugaresDisponibles.length > 0 ? 'row-eq' : ''} mb-3`}>
                    {lugaresDisponibles.length > 0 && (
                      <div>
                        <label className="form-label">
                          {area === 'Urbana' ? 'Colonia / Barrio' : 'Aldea / Caserío'}
                        </label>
                        <div className="lugar-combobox" ref={lugarRef}>
                          <div className="clase-input-row" style={{ marginBottom: 0 }}>
                            <span className="clase-search-ico">
                              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <circle cx="11" cy="11" r="8"/><path d="M21 21l-4.35-4.35"/>
                              </svg>
                            </span>
                            <input
                              ref={lugarInputRef}
                              type="text"
                              className="form-input"
                              placeholder="Buscar..."
                              value={busquedaLugar}
                              style={{ paddingLeft: 36 }}
                              onFocus={abrirDropdownLugar}
                              onChange={e => { setBusquedaLugar(e.target.value); setCodLugar(''); abrirDropdownLugar(); }}
                            />
                            {busquedaLugar && (
                              <button type="button" className="clase-clear-btn" onClick={() => { setBusquedaLugar(''); setCodLugar(''); }} title="Limpiar">×</button>
                            )}
                          </div>
                          {lugarDropdownOpen && (
                            <div className="lugar-dropdown" style={dropdownPos}>
                              {(() => {
                                const filtrados = lugaresDisponibles.filter(l =>
                                  busquedaLugar.trim() === '' ||
                                  `${l.TipoLugar} ${l.Descripcion}`.toLowerCase().includes(busquedaLugar.toLowerCase())
                                );
                                return filtrados.length === 0 ? (
                                  <div className="lugar-option-empty">Sin resultados para &ldquo;{busquedaLugar}&rdquo;</div>
                                ) : filtrados.map(l => (
                                  <div
                                    key={l.Cod_Lugar}
                                    className={`lugar-option${codLugar === String(l.Cod_Lugar) ? ' selected' : ''}`}
                                    onMouseDown={e => {
                                      e.preventDefault();
                                      setCodLugar(String(l.Cod_Lugar));
                                      setBusquedaLugar(`${l.TipoLugar} ${l.Descripcion}`);
                                      if (l.Area && l.Area.trim() !== area) setArea(l.Area.trim());
                                      setLugarDropdownOpen(false);
                                    }}
                                  >
                                    {l.TipoLugar} {l.Descripcion}
                                  </div>
                                ));
                              })()}
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                    <div>
                      <label className="form-label">Zona</label>
                      <input
                        type="number"
                        min="0"
                        className="form-input"
                        placeholder="Ej. 0, 1, 2..."
                        value={zona}
                        onChange={e => setZona(Number(e.target.value))}
                      />
                    </div>
                  </div>
                );
              })()}

              <div className="mb-3">
                <label className="form-label">Dirección / Lugar del Incidente</label>
                <div className="input-group">
                  <span className="input-group-icon">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"/>
                      <path d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"/>
                    </svg>
                  </span>
                  <input
                    type="text"
                    className="form-input"
                    placeholder="Ej. Calle principal cerca iglesia"
                    value={direccion}
                    onChange={e => setDireccion(e.target.value)}
                  />
                </div>
              </div>

              <div className="mb-4">
                <label className="form-label">
                  Ubicación en Mapa&nbsp;
                  <span style={{ fontWeight: 400, textTransform: 'none', color: '#aaa', fontSize: '.72rem' }}>
                    — clic para marcar · doble clic para zoom
                  </span>
                </label>
                <div className="map-frame">
                  {geocodificando && (
                    <div className="map-geocoding">
                      <div className="map-geocoding-dot" />
                      Buscando ubicación...
                    </div>
                  )}
                  <MapContainer center={[latitud, longitud]} zoom={14} scrollWheelZoom style={{ width: '100%', height: '100%' }}>
                    <TileLayer
                      attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                      url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                    />
                    <Marker position={[latitud, longitud]} />
                    <MapView lat={mapCenter.lat} lon={mapCenter.lon} />
                    <MapClickHandler onClic={(lat, lon) => { setLatitud(lat); setLongitud(lon); }} />
                  </MapContainer>
                </div>
                <div className="map-coords">📍 {latitud.toFixed(6)}, {longitud.toFixed(6)}</div>
                <a
                  href={`https://waze.com/ul?ll=${latitud},${longitud}&navigate=yes`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-waze"
                >
                  <img src="https://www.waze.com/favicon.ico" alt="" style={{ width: 16, height: 16 }} />
                  Abrir en Waze
                </a>
              </div>
            </div>
          </div>

          {/* ── CARD 3: DETALLES ────────────────────────────────── */}
          <div className="inc-card">
            <div className="inc-card-header">
              <div className="inc-card-num">3</div>
              <div className="inc-card-header-text">
                <h3>Detalles de la Emergencia</h3>
                <p>Urgencia, unidades requeridas y observaciones</p>
              </div>
            </div>
            <div className="inc-card-body">

              <div className="mb-3">
                <label className="form-label">Nivel de Urgencia</label>
                <div className="urg-selector">
                  {URGENCIAS.map(u => (
                    <div
                      key={u.nivel}
                      className="urg-opt"
                      style={urgencia === u.nivel ? { borderColor: u.border, background: u.bg, color: u.color } : {}}
                      onClick={() => setUrgencia(u.nivel)}
                    >
                      {u.nivel}<br />{u.label}
                    </div>
                  ))}
                </div>
              </div>

              <div className="row-eq mb-3">
                <div>
                  <label className="form-label">Unidades Requeridas</label>
                  <select className="form-input" value={unidades} onChange={e => setUnidades(e.target.value)}>
                    <option>1 Unidad</option>
                    <option>2 Unidades</option>
                    <option>3 Unidades</option>
                    <option>Todas disponibles</option>
                  </select>
                </div>
                <div>
                  <label className="form-label">Nombre Solicitante</label>
                  <input
                    type="text"
                    className="form-input"
                    placeholder="Ej. Carlos Amilcar"
                    value={nombreSolicitante}
                    onChange={e => setNombreSolicitante(e.target.value)}
                  />
                </div>
              </div>

              <div className="mb-4">
                <label className="form-label">Observaciones</label>
                <textarea
                  className="form-input"
                  rows={4}
                  style={{ resize: 'vertical' }}
                  placeholder="Describe la situación del incidente..."
                  value={observaciones}
                  onChange={e => setObservaciones(e.target.value)}
                />
              </div>

              {exito && (
                <div style={{ background: '#f0fdf4', border: '1.5px solid #86efac', borderRadius: 10, padding: '12px 16px', color: '#166534', fontWeight: 600, fontSize: 14, marginBottom: 12 }}>
                  {exito}
                </div>
              )}
              {error && <div className="error-box mb-3">{error}</div>}

              {/* Botón visible solo en mobile (en desktop va en el panel derecho) */}
              <div style={{ display: 'none' }} className="btn-registrar-mobile">
                <button className="btn-registrar" onClick={handleAbrirModal} disabled={enviando}>
                  <ExclamationCircleIcon />
                  {enviando ? 'REGISTRANDO...' : 'REGISTRAR INCIDENTE'}
                </button>
              </div>
            </div>
          </div>

        </div>

        {/* COLUMNA DERECHA — RESUMEN EN VIVO */}
        <div className="inc-summary">
          <div className="inc-summary-card">
            <div className="inc-summary-header">
              <h4>📋 Resumen del Incidente</h4>
              <p>Se actualiza en tiempo real</p>
            </div>
            <div className="inc-summary-body">
              <div className="inc-summary-row">
                <span className="inc-summary-label">Tipo</span>
                <span className="inc-summary-value">
                  {tipoSeleccionado
                    ? tipoSeleccionado.Descripcion_Servicio
                    : <span className="inc-summary-empty">Sin seleccionar</span>}
                </span>
              </div>
              <div className="inc-summary-row">
                <span className="inc-summary-label">Clase</span>
                <span className="inc-summary-value">
                  {claseSeleccionada
                    ? claseSeleccionada.Des_Clase_Servicio
                    : <span className="inc-summary-empty">Sin seleccionar</span>}
                </span>
              </div>
              <div className="inc-summary-row">
                <span className="inc-summary-label">Ubicación</span>
                <span className="inc-summary-value">
                  {muniNombre !== '—' ? `${muniNombre}, ${deptoNombre}` : <span className="inc-summary-empty">Sin seleccionar</span>}
                </span>
              </div>
              <div className="inc-summary-row">
                <span className="inc-summary-label">Dirección</span>
                <span className="inc-summary-value">
                  {direccion.trim() || <span className="inc-summary-empty">Sin ingresar</span>}
                </span>
              </div>
              <div className="inc-summary-row">
                <span className="inc-summary-label">Urgencia</span>
                <span className="inc-summary-value" style={{ color: urgSel?.color || '#1e293b' }}>
                  {urgSel ? `${urgSel.nivel} — ${urgSel.label}` : '—'}
                </span>
              </div>
              <div className="inc-summary-row">
                <span className="inc-summary-label">Unidades</span>
                <span className="inc-summary-value">{unidades}</span>
              </div>
              <div className="inc-summary-row">
                <span className="inc-summary-label">Solicitante</span>
                <span className="inc-summary-value">
                  {nombreSolicitante.trim() || <span className="inc-summary-empty">Sin ingresar</span>}
                </span>
              </div>
            </div>

            <div style={{ padding: '0 16px 16px' }}>
              <div className="ia-box" style={{ marginBottom: 14 }}>
                <span className="ia-ico">🤖</span>
                <div style={{ fontSize: '.8rem' }}>
                  <strong>Sugerencia IA:</strong> Se recomienda despachar{' '}
                  <strong>Unidad 44-1 (Autobomba)</strong> como primera respuesta.
                  Tiempo estimado: <strong>11.3 min</strong>.
                </div>
              </div>

              {exito && (
                <div style={{ background: '#f0fdf4', border: '1.5px solid #86efac', borderRadius: 10, padding: '10px 14px', color: '#166534', fontWeight: 600, fontSize: '.83rem', marginBottom: 12 }}>
                  {exito}
                </div>
              )}
              {error && <div className="error-box" style={{ marginBottom: 12 }}>{error}</div>}

              <button className="btn-registrar" onClick={handleAbrirModal} disabled={enviando}>
                <ExclamationCircleIcon style={{ width: 18, height: 18 }} />
                {enviando ? 'REGISTRANDO...' : 'REGISTRAR INCIDENTE'}
              </button>
            </div>
          </div>
        </div>

      </div>

      {/* ── MODAL CONFIRMACIÓN ──────────────────────────────────────── */}
      {modalOpen && (
        <div className="modal-overlay" onClick={() => setModalOpen(false)}>
          <div className="modal-box" onClick={e => e.stopPropagation()}>
            <div className="modal-icon">⚠️</div>
            <h3 className="modal-title">Confirmar Registro</h3>
            <p className="modal-desc">¿Estás seguro de que deseas registrar este incidente y proceder al despacho?</p>

            <div className="modal-resumen">
              <div className="modal-resumen-row">
                <span>Dirección</span>
                <strong>{direccion}</strong>
              </div>
              <div className="modal-resumen-row">
                <span>Departamento</span>
                <strong>{deptoNombre}</strong>
              </div>
              <div className="modal-resumen-row">
                <span>Municipio</span>
                <strong>{muniNombre}</strong>
              </div>
              <div className="modal-resumen-row">
                <span>Área</span>
                <strong>{area}</strong>
              </div>
              <div className="modal-resumen-row">
                <span>Urgencia</span>
                <strong style={{ color: urgSel.color }}>{urgSel.nivel} — {urgSel.label}</strong>
              </div>
              <div className="modal-resumen-row">
                <span>Unidades</span>
                <strong>{unidades}</strong>
              </div>
            </div>

            <div className="modal-actions">
              <button className="modal-btn-cancel" onClick={() => setModalOpen(false)}>Cancelar</button>
              <button className="modal-btn-confirm" onClick={handleConfirmar}>
                <ExclamationCircleIcon />
                Sí, registrar
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}